/**
 * Expense Split Selector Component
 *
 * Allows users to select split type, participants, and who paid
 */

import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectField, type SelectOption } from '@/components/ui/SelectField';
import { ThemedText, SubHeading, Caption } from '@/components/ui/Text';
import type { ExpenseSplit, SplitType } from '@/services/expenses';
import { CustomSplitEditor } from './CustomSplitEditor';
import { formatCurrency } from '@/utils/currency';
import { expenseSplitSnapshotKey } from '@/utils/expenseSplitting';
import { TripErrorBoundary } from '@/utils/errorBoundary';

type ExpenseSplitSelectorProps = {
  /** Expense id, booking id, or "new" — isolates split state when switching entities */
  splitContextId?: string;
  amount: number;
  currency: string;
  tripMembers: Array<{ userId: string; name?: string; email: string }>;
  currentUserId: string;
  initialSplit?: ExpenseSplit;
  onSplitChange: (split: ExpenseSplit | null) => void;
};

const SPLIT_TYPE_OPTIONS: SelectOption[] = [
  { value: 'none', label: "Don't split" },
  { value: 'equal', label: 'Split equally' },
  { value: 'custom', label: 'Split by custom amounts' },
];

function buildSplitFromState(
  splitType: SplitType,
  splitAmong: string[],
  paidBy: string,
  customAmounts: Record<string, number>,
): ExpenseSplit | null {
  if (splitType === 'none') return null;

  if (splitType === 'equal' && splitAmong.length > 0) {
    return { splitType: 'equal', splitAmong, paidBy };
  }

  if (splitType === 'custom' && splitAmong.length > 0 && Object.keys(customAmounts).length > 0) {
    return {
      splitType: 'custom',
      splitAmong,
      paidBy,
      splitAmounts: customAmounts,
    };
  }

  return null;
}

function ExpenseSplitSelectorInner({
  splitContextId,
  amount,
  currency,
  tripMembers,
  currentUserId,
  initialSplit,
  onSplitChange,
}: ExpenseSplitSelectorProps) {
  const theme = useTheme();
  const scaleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastNotifiedKeyRef = useRef<string>('__pending__');
  const onSplitChangeRef = useRef(onSplitChange);
  onSplitChangeRef.current = onSplitChange;

  const [splitType, setSplitType] = useState<SplitType>(initialSplit?.splitType || 'none');
  const [splitAmong, setSplitAmong] = useState<string[]>(initialSplit?.splitAmong || []);
  const [paidBy, setPaidBy] = useState<string>(initialSplit?.paidBy || currentUserId);
  const [customAmounts, setCustomAmounts] = useState<Record<string, number>>(
    initialSplit?.splitAmounts || {},
  );
  const [showCustomEditor, setShowCustomEditor] = useState(false);

  const initialSplitSyncKey = useMemo(() => {
    const scope = splitContextId ?? '__no_scope__';
    return `${scope}|${expenseSplitSnapshotKey(initialSplit ?? null)}`;
  }, [initialSplit, splitContextId]);

  const notifyParent = useCallback((split: ExpenseSplit | null) => {
    const key = expenseSplitSnapshotKey(split);
    if (key === lastNotifiedKeyRef.current) return;
    lastNotifiedKeyRef.current = key;
    onSplitChangeRef.current(split);
  }, []);

  useLayoutEffect(() => {
    if (scaleTimeoutRef.current) {
      clearTimeout(scaleTimeoutRef.current);
      scaleTimeoutRef.current = null;
    }

    const targetKey = expenseSplitSnapshotKey(initialSplit ?? null);
    if (targetKey === lastNotifiedKeyRef.current) {
      return;
    }

    lastNotifiedKeyRef.current = targetKey;

    if (!initialSplit) {
      setSplitType('none');
      setSplitAmong([]);
      setPaidBy(currentUserId);
      setCustomAmounts({});
      setShowCustomEditor(false);
      return;
    }

    setSplitType(initialSplit.splitType);
    setSplitAmong([...initialSplit.splitAmong]);
    setPaidBy(initialSplit.paidBy);
    setCustomAmounts(initialSplit.splitAmounts ? { ...initialSplit.splitAmounts } : {});
    setShowCustomEditor(false);
  }, [initialSplitSyncKey, currentUserId, initialSplit]);

  useEffect(() => {
    if (splitType !== 'custom' || showCustomEditor) return;
    if (scaleTimeoutRef.current) clearTimeout(scaleTimeoutRef.current);
    scaleTimeoutRef.current = setTimeout(() => {
      scaleTimeoutRef.current = null;
      setCustomAmounts((prev) => {
        const keys = Object.keys(prev);
        if (keys.length === 0) return prev;
        const total = Object.values(prev).reduce((s, v) => s + v, 0);
        if (Math.abs(total - amount) < 0.01) return prev;
        if (amount <= 0 || total <= 0) return prev;
        const scale = amount / total;
        const scaled: Record<string, number> = {};
        keys.forEach((k) => {
          scaled[k] = Math.round(prev[k] * scale * 100) / 100;
        });
        let sum = Object.values(scaled).reduce((a, b) => a + b, 0);
        if (keys.length) {
          const last = keys[keys.length - 1];
          scaled[last] = +(scaled[last] + (amount - sum)).toFixed(2);
        }
        return scaled;
      });
    }, 450);
    return () => {
      if (scaleTimeoutRef.current) clearTimeout(scaleTimeoutRef.current);
    };
  }, [amount, splitType, showCustomEditor]);

  useEffect(() => {
    notifyParent(buildSplitFromState(splitType, splitAmong, paidBy, customAmounts));
  }, [splitType, splitAmong, paidBy, customAmounts, notifyParent]);

  const handleSplitTypeChange = (newType: SplitType) => {
    setSplitType(newType);
    if (newType === 'none') {
      setSplitAmong([]);
      setCustomAmounts({});
    } else if (newType === 'equal') {
      setCustomAmounts({});
      setSplitAmong((prev) => (paidBy && !prev.includes(paidBy) ? [...prev, paidBy] : prev));
    }
  };

  useEffect(() => {
    if (splitType === 'none' || !paidBy) return;
    setSplitAmong((prev) => (prev.includes(paidBy) ? prev : [...prev, paidBy]));
  }, [paidBy, splitType]);

  const toggleMember = (userId: string) => {
    if (splitType === 'none') return;

    if (splitAmong.includes(userId)) {
      setSplitAmong(splitAmong.filter((id) => id !== userId));
      if (customAmounts[userId] !== undefined) {
        const newAmounts = { ...customAmounts };
        delete newAmounts[userId];
        setCustomAmounts(newAmounts);
      }
    } else {
      setSplitAmong([...splitAmong, userId]);
      if (splitType === 'custom') {
        const perPerson = amount / (splitAmong.length + 1);
        setCustomAmounts({ ...customAmounts, [userId]: perPerson });
      }
    }
  };

  const handleCustomAmountsChange = (amounts: Record<string, number>) => {
    setCustomAmounts(amounts);
  };

  const getPerPersonAmount = () => {
    if (splitAmong.length === 0) return 0;
    return amount / splitAmong.length;
  };

  const getTotalCustomAmount = () => {
    return Object.values(customAmounts).reduce((sum, amt) => sum + amt, 0);
  };

  const getMemberName = (userId: string): string => {
    const member = tripMembers.find((m) => m.userId === userId);
    if (member) {
      return member.name || member.email.split('@')[0];
    }
    return userId === currentUserId ? 'You' : 'Unknown';
  };

  return (
    <Card>
      <View style={{ gap: theme.spacing(3) }}>
        <View>
          <SubHeading style={{ marginBottom: theme.spacing(0.5) }}>Split Expense</SubHeading>
          <Caption color={theme.colors.textSecondary}>
            Divide this expense among trip members
          </Caption>
        </View>

        <SelectField
          label="Split Type"
          value={splitType}
          options={SPLIT_TYPE_OPTIONS}
          onValueChange={(value) => handleSplitTypeChange(value as SplitType)}
        />

        {splitType !== 'none' && (
          <>
            <View>
              <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
                Who paid for this expense?
              </Caption>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                {tripMembers.map((member, index) => {
                  const isSelected = paidBy === member.userId;
                  const isCurrentUser = member.userId === currentUserId;
                  let displayName = member.name || (member.email ? member.email.split('@')[0] : '');
                  if (!displayName && index === 0) {
                    displayName = 'Trip Owner';
                  }

                  return (
                    <Pressable
                      key={member.userId}
                      onPress={() => setPaidBy(member.userId)}
                      style={{
                        paddingHorizontal: theme.spacing(2),
                        paddingVertical: theme.spacing(1),
                        borderRadius: theme.radii.md,
                        backgroundColor: isSelected
                          ? theme.colors.primary
                          : theme.colors.background,
                        borderWidth: 1,
                        borderColor: isSelected
                          ? theme.colors.primary
                          : theme.colors.borderMuted,
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                      }}
                    >
                      {isSelected && (
                        <Ionicons name="checkmark" size={16} color={theme.colors.surface} />
                      )}
                      <ThemedText
                        variant="body"
                        style={{
                          color: isSelected ? theme.colors.surface : theme.colors.text,
                          fontWeight: isSelected ? '600' : '400',
                        }}
                      >
                        {isCurrentUser ? 'You' : displayName}
                      </ThemedText>
                    </Pressable>
                  );
                })}
              </View>
            </View>

            <View>
              <Caption color={theme.colors.textSecondary} style={{ marginBottom: theme.spacing(1) }}>
                Split among:
              </Caption>
              <View style={{ gap: theme.spacing(1) }}>
                {tripMembers.map((member, index) => {
                  const isSelected = splitAmong.includes(member.userId);
                  const isCurrentUser = member.userId === currentUserId;
                  let displayName = member.name || (member.email ? member.email.split('@')[0] : '');
                  if (!displayName && index === 0) {
                    displayName = 'Trip Owner';
                  }
                  const isPayer = paidBy === member.userId;
                  const isAlwaysSelected = isPayer;

                  return (
                    <Pressable
                      key={member.userId}
                      onPress={() => !isAlwaysSelected && toggleMember(member.userId)}
                      disabled={isAlwaysSelected}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: theme.spacing(1.5),
                        borderRadius: theme.radii.md,
                        backgroundColor: (isSelected || isAlwaysSelected)
                          ? theme.colors.primary + '15'
                          : theme.colors.background,
                        borderWidth: 1,
                        borderColor: (isSelected || isAlwaysSelected)
                          ? theme.colors.primary
                          : theme.colors.borderMuted,
                        opacity: isAlwaysSelected ? 0.7 : 1,
                      }}
                    >
                      <View
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: 12,
                          backgroundColor: (isSelected || isAlwaysSelected)
                            ? theme.colors.primary
                            : theme.colors.borderMuted,
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: theme.spacing(1.5),
                        }}
                      >
                        {(isSelected || isAlwaysSelected) && (
                          <Ionicons name="checkmark" size={16} color={theme.colors.surface} />
                        )}
                      </View>
                      <View style={{ flex: 1 }}>
                        <ThemedText variant="body">
                          {isCurrentUser ? 'You' : displayName}
                          {isPayer ? ' (paid)' : ''}
                        </ThemedText>
                      </View>
                      {splitType === 'equal' && (isSelected || isAlwaysSelected) && (
                        <ThemedText variant="bodyStrong" color={theme.colors.primary}>
                          {formatCurrency(getPerPersonAmount(), currency)}
                        </ThemedText>
                      )}
                    </Pressable>
                  );
                })}
              </View>

              {splitType === 'equal' && splitAmong.length > 0 && (
                <View
                  style={{
                    marginTop: theme.spacing(1.5),
                    padding: theme.spacing(1.5),
                    backgroundColor: theme.colors.primary + '08',
                    borderRadius: theme.radii.md,
                  }}
                >
                  <Caption color={theme.colors.textSecondary}>
                    Each person owes: {formatCurrency(getPerPersonAmount(), currency)}
                  </Caption>
                </View>
              )}

              {splitType === 'custom' && (
                <>
                  <Button
                    label="Set Custom Amounts"
                    variant="secondary"
                    onPress={() => setShowCustomEditor(true)}
                    style={{ marginTop: theme.spacing(1.5) }}
                  />
                  {Object.keys(customAmounts).length > 0 && (
                    <View
                      style={{
                        marginTop: theme.spacing(1.5),
                        padding: theme.spacing(1.5),
                        backgroundColor: theme.colors.background,
                        borderRadius: theme.radii.md,
                        borderWidth: 1,
                        borderColor:
                          Math.abs(getTotalCustomAmount() - amount) < 0.01
                            ? theme.colors.success || '#34C759'
                            : theme.colors.error || '#FF3B30',
                      }}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Caption color={theme.colors.textSecondary}>Total split:</Caption>
                        <ThemedText
                          variant="bodyStrong"
                          color={
                            Math.abs(getTotalCustomAmount() - amount) < 0.01
                              ? theme.colors.success || '#34C759'
                              : theme.colors.error || '#FF3B30'
                          }
                        >
                          {formatCurrency(getTotalCustomAmount(), currency)} /{' '}
                          {formatCurrency(amount, currency)}
                        </ThemedText>
                      </View>
                      {Math.abs(getTotalCustomAmount() - amount) >= 0.01 && (
                        <Caption
                          color={theme.colors.error || '#FF3B30'}
                          style={{ marginTop: theme.spacing(0.5) }}
                        >
                          Amounts must equal expense total
                        </Caption>
                      )}
                    </View>
                  )}
                </>
              )}
            </View>
          </>
        )}
      </View>

      {showCustomEditor && (
        <CustomSplitEditor
          key={`custom-split-${splitContextId ?? 'default'}`}
          visible={showCustomEditor}
          onClose={() => setShowCustomEditor(false)}
          amount={amount}
          currency={currency}
          tripMembers={tripMembers.filter((m) => splitAmong.includes(m.userId))}
          currentUserId={currentUserId}
          initialAmounts={customAmounts}
          onAmountsChange={handleCustomAmountsChange}
        />
      )}
    </Card>
  );
}

function SplitSelectorFallback() {
  const theme = useTheme();
  return (
    <Card variant="outlined" style={{ padding: theme.spacing(1.5) }}>
      <Caption color={theme.colors.textSecondary}>
        Split options are temporarily unavailable. You can still save this expense without splitting.
      </Caption>
    </Card>
  );
}

export function ExpenseSplitSelector(props: ExpenseSplitSelectorProps) {
  return (
    <TripErrorBoundary name="ExpenseSplitSelector" fallback={<SplitSelectorFallback />}>
      <ExpenseSplitSelectorInner {...props} />
    </TripErrorBoundary>
  );
}
